import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import NavbarAdmin from './NavbarAdmin';
import CVListAdmin from './CVListAdmin';
import { useAuth } from '../../AuthContext';
import './Dashboard.css';
import WICVLoader from './../WICVLoader';
import ErrorModal from './../ErrorModal';

const DashboardAdmin = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(user || {});
  const [cvs, setCvs] = useState([]);
  const [filteredCvs, setFilteredCvs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!user?.idUtilisateur) return;

    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const base = `${process.env.REACT_APP_API_BASE_URL}/api/user/${user.idUtilisateur}`;
      const [profileRes, cvsRes, notificationsRes, accountsRes] = await Promise.all([
        axios.get(base, { headers }),
        axios.get(`${base}/cvs`, { headers }),
        axios.get(`${base}/notifications`, { headers }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/utilisateurs/demandes-validation`, { headers })
      ]);

      setProfile(profileRes.data);
      setCvs(cvsRes.data);
      setFilteredCvs(cvsRes.data);
      setNotifications(notificationsRes.data);
      setPendingAccounts(accountsRes.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateAccount = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/utilisateurs/${userId}/valider`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingAccounts(pendingAccounts.filter(acc => acc.idUtilisateur !== userId));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const fileInputRefWikeys = useRef(null);
  const fileInputRefWissal = useRef(null);
  const fileInputRefWikeysAnglais = useRef(null);

  const handleGenerateCVWikeysClick = () => {
    fileInputRefWikeys.current.click();
  };

  const handleGenerateCVWissalClick = () => {
    fileInputRefWissal.current.click();
  };

  const handleGenerateCVWikeysAnglaisClick = () => {
    fileInputRefWikeysAnglais.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', user.email); // Ajouter l'email

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/cvs/process`, // URL modifiée
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      await fetchData();
    } catch (err) {
      console.error('Erreur détaillée:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };
   
  const handleFileChangeWissal = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', user.email); // Ajouter l'email

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/cvs/processWissal`, // URL modifiée
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      await fetchData();
    } catch (err) {
      console.error('Erreur détaillée:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChangeWikeysAnglais = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', user.email); // Ajouter l'email

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/cvs/processWikeysAnglais`, // URL modifiée
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      await fetchData();
    } catch (err) {
      console.error('Erreur détaillée:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (keyword) => {
    const filtered = cvs.filter((cv) =>
      cv.nomFichier.toLowerCase().includes(keyword.toLowerCase())
    );
    setFilteredCvs(filtered);
  };

  useEffect(() => {
    fetchData();
  }, [user?.idUtilisateur]);

  if (loading) return <WICVLoader />;

  return (
    <div className="body-dushboard">
      <ErrorModal message={error} onClose={() => setError(null)} />
      <div className="dashboard-container">
        <NavbarAdmin
          user={profile}
          notifications={notifications}
          onLogout={logout}
          onSearchChange={handleSearchChange}
        />

        <main className="dashboard-main">
          <div className="dashboard-welcome" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h1 className="dashboard-welcome-title">
              Tableau de bord Administrateur
            </h1>
            <p className="dashboard-welcome-subtitle">
              Gestion des CVs et des comptes utilisateurs
            </p>
          </div>

          <div className="dashboard-generate-btn-container" style={{ width: '100%' }}>
            <button 
              className="dashboard-generate-btn"
              onClick={() => window.location.href = '/validate-accounts'}
            >
              <i className="fas fa-user-check dashboard-generate-icon"></i>
              Valider les nouveaux comptes
              {pendingAccounts.length > 0 && (
                <span className="dashboard-pending-badge">{pendingAccounts.length}</span>
              )}
            </button>

            {/* Bouton Wikeys */}
            <button className="dashboard-generate-btn" onClick={handleGenerateCVWikeysClick}>
              Générer un nouveau CV Wikeys
            </button>
            <input
              type="file"
              ref={fileInputRefWikeys}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.docx,.doc,.txt"
            />

            {/* Bouton Wissal */}
            <button className="dashboard-generate-btn" onClick={handleGenerateCVWissalClick}>
              Générer un nouveau CV Wissal
            </button>
            <input
              type="file"
              ref={fileInputRefWissal}
              onChange={handleFileChangeWissal}
              style={{ display: 'none' }}
              accept=".pdf,.docx,.doc,.txt"
            />

            {/* Bouton Wikeys Anglais */}
            <button className="dashboard-generate-btn" onClick={handleGenerateCVWikeysAnglaisClick}>
              Générer un nouveau CV Wikeys en anglais
            </button>
            <input
              type="file"
              ref={fileInputRefWikeysAnglais}
              onChange={handleFileChangeWikeysAnglais}
              style={{ display: 'none' }}
              accept=".pdf,.docx,.doc,.txt"
            />
          </div>

          <div className="dashboard-scrollable-container">
            <CVListAdmin cvs={filteredCvs} refreshData={fetchData} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardAdmin;